import { useCallback, useMemo } from "react";
import { useReservationStore } from "../../../stores/useReservationStore";
import { useCountryCodes } from "./useCountryCodes";
import type { ReservationFormValues } from "../reservation-page.schema";
import { getPhoneDigits, PARTIAL_DATE_SUFFIX } from "../reservation-page.schema";
import type { Ticket, TicketPersonFormField } from "../../../types/reservation";
import { ReservationIntent } from "../../../common/enums/ReservationIntent";
import { filterNameInput } from "../../../utils/Utils";
import { getProductLines } from "../utils/productQuantities";

const NAME_MAPS = new Set(["FirstName", "LastName"]);

export function useReservationPayload(
  tickets: Ticket[],
  ticketPersonFormFields: TicketPersonFormField[] = [],
  intent: ReservationIntent = ReservationIntent.Finalize,
) {
  const { reservationId, products, ticketPersonData } = useReservationStore();
  const { countryCodes } = useCountryCodes();

  // Resolves mobile and mobileCountryCode for the payload.
  // Logged-in users have the prefix baked into phonenumber ("357 6980000000"),
  // so we split on the space and look up the country code from there.
  // Not-logged-in users have the prefix in the dropdown (+prefix form value).
  const getMobileFields = useCallback(
    (mobile: string, formPrefix: string | undefined) => {
      if (mobile?.includes(" ")) {
        const spaceIdx = mobile.indexOf(" ");
        const prefix = mobile.slice(0, spaceIdx);
        const number = mobile.slice(spaceIdx + 1);
        const code = countryCodes.find((o) => o.prefix === prefix)?.code;
        return { mobile: number, mobileCountryCode: code };
      }
      const code = formPrefix
        ? countryCodes.find((o) => `+${o.prefix}` === formPrefix)?.code
        : undefined;
      return { mobile, mobileCountryCode: code };
    },
    [countryCodes],
  );

  const getSelectedProducts = useCallback(
    () =>
      getProductLines(products).map((line) => ({
        productId: line.product.id,
        optionId: line.option.id,
        quantity: line.qty,
        ticketId: line.ticketId,
      })),
    [products],
  );

  const nameAttributeIds = useMemo(
    () =>
      new Set(
        ticketPersonFormFields
          .filter(
            (f) =>
              f.ticketPersonMap !== null && NAME_MAPS.has(f.ticketPersonMap),
          )
          .map((f) => f.dynamicAttributeId),
      ),
    [ticketPersonFormFields],
  );

  const phoneAttributeIds = useMemo(
    () =>
      new Set(
        ticketPersonFormFields
          .filter((f) => f.ticketPersonMap === "Phone")
          .map((f) => f.dynamicAttributeId),
      ),
    [ticketPersonFormFields],
  );

  const freeTicketOptionalAttributeIds = useMemo(
    () =>
      new Set(
        ticketPersonFormFields
          .filter((f) => f.notRequiredForFreeTickets)
          .map((f) => f.dynamicAttributeId),
      ),
    [ticketPersonFormFields],
  );

  const getTicketPersons = useCallback(
    () =>
      tickets.map((ticket) => ({
        ticketId: ticket.id,
        seatId: ticket.seat?.id,
        attributes: Object.entries(ticketPersonData[ticket.id] ?? {})
          .filter(([attributeId, value]) => {
            if (attributeId.endsWith(PARTIAL_DATE_SUFFIX)) return false;
            if (value === null || value === "") return false;
            if (ticket.price === 0 && freeTicketOptionalAttributeIds.has(attributeId))
              return false;
            // CC-only phone value (no digits typed yet) — treat as empty.
            return phoneAttributeIds.has(attributeId)
              ? !!getPhoneDigits(value)
              : true;
          })
          .map(([attributeId, dynamicValue]) => ({
            ticketId: ticket.id,
            attributeId,
            dynamicValue: nameAttributeIds.has(attributeId)
              ? filterNameInput(dynamicValue ?? "")
              : dynamicValue,
          })),
      })),
    [tickets, ticketPersonData, nameAttributeIds, phoneAttributeIds, freeTicketOptionalAttributeIds],
  );

  const buildPayload = useCallback(
    (
      values: ReservationFormValues,
      paymentMethod: string,
    ) => {
      const {
        contact,
        shipping,
        requiresShipping,
        billing,
        receiveUpdates,
        acceptTerms,
        merchantTerms,
        donationAmount,
      } = values;

      // For receipt, only send the type — the invoice fields are irrelevant.
      // For invoice, strip isGreekCompany (UI-only flag used by superRefine to switch validation modes).
      const billingPayload =
        billing.type === "receipt"
          ? { type: billing.type }
          : (({ isGreekCompany: _isGreekCompany, ...rest }) => rest)(billing as any);

      const { mobile, mobileCountryCode } = getMobileFields(
        contact.mobile,
        contact.mobileCountryCode,
      );

      const shippingContact = requiresShipping
        ? {
            firstName: contact.firstName,
            lastName: contact.lastName,
            mobile: contact.mobile,
            address: `${shipping.street ?? ""} ${shipping.number ?? ""}`.trim(),
            city: shipping.city ?? "",
            country: shipping.country ?? "",
            area: shipping.area ?? "",
            postalCode: shipping.postcode ?? "",
          }
        : undefined;

      const acceptedTerms = Object.entries(merchantTerms ?? {})
        .filter(([, accepted]) => accepted)
        .map(([termsId]) => Number(termsId));

      const payload = {
        reservationId,
        contact: {
          ...contact,
          mobile,
          mobileCountryCode,
        },
        billing: billingPayload,
        receiveUpdates,
        acceptTerms,
        ...(shippingContact && { shippingContact }),
        products: getSelectedProducts(),
        donationAmount: donationAmount > 0 ? donationAmount : null,
        ticketPersons: getTicketPersons(),
        acceptedTerms,
        paymentMethod,
        intent,
        caller: btoa(window.location.href),
        sendSms: false, //always false TBD
      };
      return payload;
    },
    [reservationId, getSelectedProducts, getTicketPersons, getMobileFields, intent],
  );

  return { buildPayload };
}
